import { useEffect, useId, useRef, useState } from 'react';
import { DashboardFilterBar, FilterSlot } from '@/components/dashboards/DashboardFilterBar';
import {
  TargetTileGrid,
  TileFailureStrip,
  tileState,
  type TileState,
} from '@/components/dashboards/TargetTiles';
import {
  TargetWindowPicker,
  initialWindowPickerState,
  parseWindowPickerState,
  windowChoiceOf,
  type WindowPickerState,
} from '@/components/dashboards/TargetWindowPicker';
import {
  Combobox,
  Field,
  MultiCombobox,
  Panel,
  PanelHeader,
  SegmentedControl,
} from '@/components/ui';
import { TARGET_BILLING_REGIONS, useBillingRegionLabels } from '@/lib/billing-region';
import {
  CONVERSION_APPROXIMATE_NOTE,
  FUNNEL_METRICS,
  conversionNote,
  excludedByCurrencyNote,
  targetMetricLabel,
  type ExcludedNote,
  type FunnelMetric,
} from '@/lib/targets';
import type { TargetActualRowsParams, TargetBillingRegion, TargetFxMode } from '@/lib/types';
import { MetricDrilldownModal } from '@/components/dashboards/drilldown/MetricDrilldownModal';
import {
  contributionBasis,
  rollUpContributions,
  type MemberContributionInput,
} from '@/lib/team-contribution';
import {
  resolveWindow,
  teamActualsSubject,
  useDefaultTargetPeriod,
  useMemberFigures,
  useTargetActuals,
  useTeamMembers,
  useUserGroups,
  type ActualsState,
  type TeamSubject,
} from '@/lib/use-team-performance';
import { AttributionNote } from './AttributionNote';
import { ContributionPanel } from './ContributionPanel';
import { TargetsCommissionPanel } from './TargetsCommissionPanel';
import { useRememberedState } from '@/lib/filter-memory/use-filter-memory';
import { userReportingCurrency } from '@/lib/reporting-currency';
import { useAuth } from '@/lib/use-auth';
import {
  ANALYTICS_CURRENCIES,
  ANALYTICS_CURRENCY_OPTIONS,
  DEFAULT_ANALYTICS_CURRENCY,
  DEFAULT_FX_MODE,
  DEFAULT_TEAM_SLUG,
  FIGURE_MODE_OPTIONS,
  FX_MODE_OPTIONS,
  parseFxMode,
  WHOLE_ORGANISATION,
  actualsCurrency,
  panelDescription,
  personName,
  teamLabel,
  teamValue,
  type FigureMode,
  type MetricRolls,
} from './financial-shared';

/**
 * FINANCIAL ANALYTICS — one team's five figures over one window, and each
 * member's share of the team's target.
 *
 * ⚠️ **THIS IS THE TEAM HALF OF THE DELETED MANAGEMENT DASHBOARD, MOVED AND
 * REDESIGNED — the CONTENT is unchanged and the LAYOUT is not.** Owner's
 * instruction, 2026-09-11: *"the financial analytics will be same as team
 * performance that we deleted absolutely same so bring all the cards, tables
 * everything in the same way but make the ui and ux more simple and good
 * looking."* So nothing was dropped and nothing was invented; what moved is
 * where the controls live and how the table reads.
 *
 * ⚠️ **`IndividualPanel` DID NOT COME BACK, and neither did anything only it
 * used.** The Individual Performance tab is deleted. Every state and fetch it
 * owned — `personId`, the `subjectType: 'user'` actuals read, its 403 strip — is
 * absent deliberately, not forgotten.
 *
 * ── SELF-CONTAINED BY CONTRACT ──────────────────────────────────────────────
 *
 * Takes no props and owns its own state and its own reads. **It renders no
 * `PageHeader` and performs no permission check** — the page owns the title, the
 * tab strip and `NAV_SURFACE_MODULE.dashboardManagement`, and a second check
 * here would be a second place for the two to disagree.
 *
 * ── ✅ THE CURRENCY IS WIRED END TO END — 2026-09-11 ────────────────────────
 *
 * The GBP-only ruling of 2026-09-02 is withdrawn (owner, 2026-09-11) — see
 * `ANALYTICS_CURRENCIES`. The control below is built, its choice reaches every
 * formatter on this tab, **and it reaches the REQUEST**: both hooks take the
 * currency as a fourth argument and both join it to their cache KEY, so
 * switching refetches instead of relabelling the figures already on screen.
 *
 * *SUPERSEDED, the whole section, headed "THE TWO THINGS THAT ARE NOT WIRED
 * YET": "**It does NOT reach the request**, and it cannot from inside this
 * folder: `useTargetActuals(subject, window, enabled)` — no currency argument;
 * `useMemberFigures(members, window, enabled)` — no currency argument. Both
 * live in `src/lib/use-team-performance.ts`, which this work does not own …
 * 🔴 **AND `TargetTiles` HARDCODES `DASHBOARD_CURRENCY` INTERNALLY.** The tiles
 * are correct only while the server measures in GBP. **The hook change and a
 * currency prop on `TileFigures` must land in the same release**, or a reader
 * who picks USD gets USD amounts with `GBP` printed after them."*
 *
 * **All three claims are false, and they were falsified by the release they
 * asked for.** `useTargetActuals` and `useMemberFigures` each grew a fourth
 * `baseCurrency` parameter, and `TileFigures` takes `currency` as a PROP — its
 * own header records the change. The "same release" this warned about is the
 * one that shipped; nothing here is pending.
 *
 * ⚠️ **WHAT STILL HOLDS, and it is the half worth keeping: the figures are
 * labelled with the currency the SERVER SAYS IT MEASURED IN, never the one the
 * picker is set to.** `actualsCurrency()` reads `measuredInCurrency`, the
 * `TargetTileGrid` call below is passed that and not `currency`, and a strip
 * says so wherever the two disagree — which is what keeps a GBP figure from
 * being printed with `USD` after it against a server predating the parameter.
 *
 * ── 🔴 THE BILLING REGION IS A SECOND AXIS, BESIDE THE TEAM ────────────────
 *
 * Owner's decision: **a team's targets are set PER BILLING REGION, not
 * overall** — see `GroupTargetsCard`, where the field is required. So this tab
 * needs a way to ask *which band*, and picking one narrows the FIGURES as well
 * as selecting the target: the alternative is a regional target printed beside a
 * whole-business actual, with the difference labelled attainment.
 *
 * ⚠️ **BESIDE the Team picker, never instead of it.** Team and region are two
 * independent questions — *whose figures* and *which companies* — and one
 * control cannot answer both. Both are in the primary row.
 *
 * ⚠️ **`null` is EVERY band and is the default; `'none'` is the fifth BAND.**
 * The unbanded companies — those with no country recorded — are a real narrowing
 * a reader can choose. The two are one row apart in the picker and mean opposite
 * things.
 *
 * ⚠️ **It reaches BOTH hooks and therefore the tiles, the contribution table and
 * the excluded-rows strip together.** A region reaching one and not the other is
 * a breakdown that cannot add up to its own headline, reported as a residual
 * belonging to nobody.
 *
 * ⚠️ **`ContributionPanel`'s OWN caption does not name the band**, and that file
 * is not this one's to change. The panel above it does, and the filter bar shows
 * the control — but if that table is ever read on its own, its caption is the
 * gap to close.
 *
 * ⚠️ **A READER HITTING THIS HEADER GETS THE CONTRACT FROM IT AND GOES NO
 * FURTHER**, which is why this was corrected here rather than only at the
 * `useTargetActuals` call, where a ✅ RESOLVED note had sat ~90 lines below a
 * header still asserting the opposite. **A file that contradicts itself is
 * worse than one that is merely stale**: the reader who checks finds agreement
 * with whichever half they read first, and the header is always first.
 */
export function FinancialAnalyticsPanel() {
  /*
   * ⚠️ **ONE window for the whole tab.** Both panels answer "over what period",
   * and two pickers that could disagree would let somebody compare a team total
   * with a member breakdown taken over a different range and see nothing saying
   * so.
   */
  const [picker, setPicker] = useRememberedState<WindowPickerState>(
    'financial-window',
    initialWindowPickerState,
    parseWindowPickerState,
  );
  const [subject, setSubject] = useState<TeamSubject>(WHOLE_ORGANISATION);
  /*
   * The team the reader last CHOSE, by `teamValue` — `null` until they choose
   * one (filter memory, owner 2026-10-16). Resolved against the loaded groups
   * by the opening-team effect below; a remembered group that no longer exists
   * falls back to the default team.
   */
  const [chosenTeam, setChosenTeam] = useRememberedState<string | null>(
    'financial-team',
    null,
    (raw) => (raw === null || typeof raw === 'string' ? raw : undefined),
  );
  /*
   * Whether the opening team has been decided. See the effect below — it is a
   * ref and not state because settling must not itself cause a render.
   */
  const teamSettled = useRef(false);
  /** Chosen member ids. **Empty is every member**, never none — see `ContributionPanel`. */
  const [memberFilter, setMemberFilter] = useRememberedState<number[]>(
    'financial-members',
    [],
    (raw) =>
      Array.isArray(raw) && raw.every((id) => typeof id === 'number') ? (raw as number[]) : undefined,
  );
  const [figureMode, setFigureMode] = useRememberedState<FigureMode>(
    'financial-figure-mode',
    'value',
    (raw) => (raw === 'value' || raw === 'percentage' ? raw : undefined),
  );
  /*
   * Defaults to the signed-in person's reporting currency (owner 2026-10-28)
   * when it is one this tab offers, else the server's own base currency. A
   * remembered pick still wins.
   */
  const { user } = useAuth();
  const [currency, setCurrency] = useRememberedState<string>(
    'financial-currency',
    userReportingCurrency(user, ANALYTICS_CURRENCIES) ?? DEFAULT_ANALYTICS_CURRENCY,
    (raw) => (typeof raw === 'string' && ANALYTICS_CURRENCIES.includes(raw) ? raw : undefined),
  );
  /*
   * HOW OTHER CURRENCIES ARE CONVERTED (owner 2026-10-28) — remembered per
   * person exactly as the currency is. Defaults to `DEFAULT_FX_MODE`
   * (`period_end`, Xero's P&L basis, owner 2026-10-28); a remembered pick
   * still wins. ⚠️ **The team figures only**: "Targets & commission" is
   * commission maths and stays on document dates, so it is not passed there.
   */
  const [fxMode, setFxMode] = useRememberedState<TargetFxMode>(
    'financial-fx-mode',
    DEFAULT_FX_MODE,
    parseFxMode,
  );
  /*
   * 🔴 **WHICH BILLING REGION THE FIGURES DESCRIBE — `null` IS EVERY REGION, AND
   * IT IS THE DEFAULT.**
   *
   * Team targets are set PER REGION (owner's decision; see `GroupTargetsCard`),
   * so a reader comparing a team against its target has to be able to say which
   * band — and choosing one narrows the FIGURES as well, or the tiles would put
   * a regional target beside a whole-business actual and call the difference
   * attainment.
   *
   * ⚠️ **`null` IS NOT A SIXTH BAND.** `'none'` is the fifth — the unbanded
   * companies, those with no country recorded — and it is a real narrowing. This
   * is the absence of one, which is why it is the picker's empty option and why
   * it sends no parameter at all.
   *
   * ⛔ **IT OPENS UNNARROWED, AND IT MAY NOT OPEN ON A BAND.** "Send no
   * parameter" and "send `united_kingdom`" are different questions, and one of
   * the five would be a figure understating the business by default under a
   * heading that named no region until somebody looked. Same rule the currency
   * beside it keeps: nothing changes for anybody until they touch the control.
   */
  const [region, setRegion] = useRememberedState<TargetBillingRegion | null>(
    'financial-region',
    null,
    (raw) =>
      raw === null || TARGET_BILLING_REGIONS.includes(raw as TargetBillingRegion)
        ? (raw as TargetBillingRegion | null)
        : undefined,
  );

  /**
   * The five bands this picker offers, in the master's own order.
   *
   * 🔴 **`BILLING_REGIONS` IS THE FOUR COMPANY BANDS; `'none'` IS THE FIFTH.**
   * `TargetBillingRegion` is `CompanyBillingRegion | 'none'`, so a picker built
   * from `BILLING_REGIONS` alone silently omits the unbanded companies — and on
   * a dashboard that is a population a reader can never ask about, with nothing
   * saying it is missing. **This list is what the control below reads**, which is
   * the whole reason it exists as a name rather than as a literal inside the JSX.
   *
   * ⚠️ **THE COMPOSITION MOVED — 2026-09-14.** *SUPERSEDED: `[...BILLING_REGIONS,
   * BILLING_REGION_NONE]`, composed here.* `GroupTargetsCard` composed the
   * identical list for its own picker, so the two were two places a sixth band
   * had to arrive and one of them would have been missed. It is
   * `TARGET_BILLING_REGIONS` now — one owner, and a member added there reaches
   * both pickers on its own.
   */
  const regionOptions: readonly TargetBillingRegion[] = TARGET_BILLING_REGIONS;

  /*
   * 🔴 **TWO SPELLINGS OF "NO NARROWING", CONVERTED ONCE, HERE — AND IT IS THE
   * VALUE BOTH HOOKS ARE GIVEN.** This panel holds `null` because a nullable band
   * reads as a choice that has not been made; the hooks declare
   * `billingRegion?: TargetBillingRegion | null`.
   *
   * ⚠️ **THE HOOKS FOLD BOTH, AND THAT IS MEASURED RATHER THAN ASSUMED.**
   * *SUPERSEDED: "the hooks take `undefined` because that is what they omit the
   * query parameter on".* `useTargetActuals` and `useMemberFigures` each build
   * their region query as `billingRegion === undefined || billingRegion === null
   * ? {} : { billingRegion }`, and each keys its cache on `billingRegion ?? ''` —
   * so `null` was never going to reach a query builder that would serialise it,
   * and the conversion here is belt and braces rather than the guard.
   *
   * ⛔ **What is NOT interchangeable, at either end, is `'none'`.** The unbanded
   * companies are a real and much smaller population; omitting the key asks for
   * everything. The hooks key them `none` against `''` for exactly that reason,
   * so the two can never share a cached answer.
   */
  const regionQuery = region ?? undefined;

  const periodState = useDefaultTargetPeriod();
  const choice = windowChoiceOf(picker);
  const blocked = choice.kind === 'none';
  const measuredWindow = resolveWindow(choice, periodState.period);
  /*
   * 🔴 **"NOTHING TO REPORT ON" IS THE RESOLVED WINDOW BEING ABSENT, AND IT IS
   * NOT `periodState.noPeriod` — which is what this passed until step one of the
   * picker shipped.** *SUPERSEDED: `noPeriod: periodState.noPeriod`.* That flag
   * answers *no catalogued period covers TODAY*; since the cycle chooser landed
   * a reader can name a cycle that is perfectly real while the flag stays
   * `true`, because a chosen cycle arrives as `{kind: 'window'}` and never
   * reaches the hook's default at all. Passing it through blanked all five tiles
   * over a window somebody had deliberately chosen — no error, no cause named,
   * just a dashboard that looked like it held no data, which is this repo's
   * an-empty-render-and-an-outage-are-one-screen failure. `resolveWindow` says
   * so at its own definition, and is the only honest answer.
   *
   * ⚠️ **The two guards make this honest ON ITS OWN TERMS rather than by
   * `tileState`'s ordering.** That reducer does test `blocked`, `loading` and
   * `error` ahead of `noPeriod` (verified in `TargetTiles.tsx`), and its
   * `loading` and `error` are each OR-ed with the figures' own — so through THIS
   * call path the guards change no outcome. They are here because while the
   * period list is in flight `period` is `null` and an untouched picker resolves
   * to `null` with it, which is *not yet known* rather than *nothing to report
   * on*: a reader of this variable must not have to know a reducer's precedence
   * to be right about what it claims.
   */
  const noPeriod = !periodState.loading && periodState.error === null && measuredWindow === null;
  /*
   * The cycle period the Targets & commission section reads (owner 2026-10-27):
   * the chosen cycle, else the default period, under every mode cut from a cycle
   * (annual, quarter, month) — the same precedence as the UP dashboard's
   * commission strip. A custom range names no cycle, so it is `null`.
   */
  const commissionPeriodId =
    picker.mode === 'range' ? null : (picker.period?.id ?? periodState.period?.id ?? null);

  const groupsState = useUserGroups();
  const teams: TeamSubject[] = [
    WHOLE_ORGANISATION,
    ...groupsState.groups.map(
      (group): TeamSubject => ({
        kind: 'group',
        groupId: group.id,
        slug: group.slug,
        name: group.name,
      }),
    ),
  ];

  /*
   * Open on the account-lead team rather than on the whole organisation.
   *
   * ⚠️ **IT CANNOT BE THE `useState` INITIALISER.** The groups arrive over the
   * wire, so at first render there is no group to name — the id, slug and label
   * a `TeamSubject` needs do not exist yet. The default is therefore applied
   * once, on the first render after the list has settled.
   *
   * ⚠️ **IT RUNS ONCE AND THEN NEVER AGAIN, WHICH IS WHAT KEEPS IT A DEFAULT.**
   * The naive version — "whenever the subject is the organisation, switch to
   * account lead" — cannot be moved off: the reader picks the whole
   * organisation, the condition is true again, the default re-applies, and the
   * control reads as broken. Same failure, and the same fix, as `/projects`
   * opening on the current cycle.
   *
   * ⚠️ **The ref is set BEFORE the outcome is examined**, so a failed read or a
   * missing group settles the question too. Retrying would re-arm the override
   * behind somebody who had meanwhile chosen a team.
   *
   * There is no race with the reader here: the Team `<select>` is `disabled`
   * while `groupsState.loading`, so nobody can have chosen anything before this
   * runs. That is the only reason no "has the user touched it" flag is needed,
   * and it is a fact about the filter bar below — if that `disabled` ever goes,
   * this needs one.
   */
  useEffect(() => {
    if (teamSettled.current || groupsState.loading) return;
    teamSettled.current = true;
    /*
     * A list that failed to load is not evidence that the group is absent, and
     * this app must not act as though it were. Stand down and leave the tab on
     * the whole organisation, which is a real subject that needs no group list.
     */
    if (groupsState.error !== null) {
      if (chosenTeam !== 'org') setMemberFilter([]);
      return;
    }
    if (chosenTeam === 'org') return;
    const remembered =
      chosenTeam === null
        ? undefined
        : groupsState.groups.find((group) => String(group.id) === chosenTeam);
    if (remembered !== undefined) {
      setSubject({
        kind: 'group',
        groupId: remembered.id,
        slug: remembered.slug,
        name: remembered.name,
      });
      return;
    }
    // Nothing remembered, or a team that is gone: its member ids mean nothing.
    if (chosenTeam !== null) setMemberFilter([]);
    const preferred = groupsState.groups.find((group) => group.slug === DEFAULT_TEAM_SLUG);
    if (preferred === undefined) return;
    setSubject({
      kind: 'group',
      groupId: preferred.id,
      slug: preferred.slug,
      name: preferred.name,
    });
  }, [groupsState.loading, groupsState.error, groupsState.groups, chosenTeam, setMemberFilter]);

  /*
   * ✅ **RESOLVED 2026-09-11.** Both hooks take the currency and both join it to
   * their cache KEY, so switching it refetches rather than relabelling the
   * figures already on screen.
   *
   * ⚠️ **`enabled` IS THE THIRD ARGUMENT ON BOTH AND THE CURRENCY IS THE
   * FOURTH.** `useTargetActuals` defaults `enabled` to `true`, so it has to be
   * passed explicitly here to reach the currency behind it — dropping it would
   * be a positional argument landing in the wrong parameter, which typechecks
   * when both are strings and does not when one is a boolean. It does not,
   * here, which is why this reads the way it does.
   */
  /*
   * 🔴 **THE REGION IS THE FIFTH ARGUMENT ON BOTH, AND IT MUST JOIN THEIR CACHE
   * KEYS EXACTLY AS THE CURRENCY DOES.** Without that, switching band leaves the
   * previous band's figures on screen under the new band's caption — a wrong
   * number wearing a plausible label, which is the failure the currency's own
   * note describes and is strictly worse than a blank tile.
   *
   * ⚠️ **BOTH HOOKS, OR THE TABLE CONTRADICTS THE TILES.** The team total comes
   * from the first and every member's share from the second, so a region reaching
   * one and not the other produces a breakdown that cannot add up to the headline
   * — and the contribution table's reconciliation footer would report the
   * difference as a residual belonging to nobody.
   */
  const teamFigures = useTargetActuals(
    teamActualsSubject(subject),
    measuredWindow,
    true,
    currency,
    regionQuery,
    fxMode,
  );
  /*
   * DRILL-DOWN (owner 2026-10-28) — ONE modal for the tab, opened from a team
   * tile or from a member's figure in "Contribution by member". `params` is
   * always the EXACT query the clicked figure was measured with plus the metric
   * — `teamFigures.actualsQuery` for a tile, `memberFigures.queryByUser[id]`
   * for a member — one copy of the rule, so the rows reconcile to the figure.
   */
  const [drill, setDrill] = useState<{
    metric: FunnelMetric;
    params: TargetActualRowsParams;
    title: string;
  } | null>(null);
  const membersState = useTeamMembers(subject);
  const memberFigures = useMemberFigures(
    membersState.members,
    measuredWindow,
    !blocked,
    currency,
    regionQuery,
    fxMode,
  );

  /*
   * ⚠️ **`rollUpContributions` IS CALLED ONCE PER METRIC, WITH ITS SEMANTICS PER
   * METRIC UNCHANGED.** Nothing is summed across metrics and nothing may be:
   * `won_deals` is a COUNT and the other four are money, so a figure spanning
   * them would be an addition of two different units.
   */
  const rolls = Object.fromEntries(
    FUNNEL_METRICS.map((m) => {
      const teamRow = teamFigures.byMetric[m];
      const memberInputs: MemberContributionInput[] = membersState.members.map((member) => ({
        userId: member.id,
        name: personName(member),
        status: memberFigures.loading
          ? 'loading'
          : (memberFigures.statusByUser[member.id] ?? 'error'),
        row: memberFigures.byUser[member.id]?.byMetric[m],
        message: memberFigures.messageByUser[member.id] ?? null,
      }));
      return [
        m,
        rollUpContributions(
          contributionBasis(teamFigures.attribution, teamRow),
          teamRow,
          memberInputs,
        ),
      ];
    }),
  ) as MetricRolls;

  /*
   * ⚠️ **Organisation queries only.** On a group it is `null` for every metric
   * and renders as nothing — deliberately, because a group total is already
   * narrowed to its members, so the subset would read `0.00` for ever: a figure
   * that can only ever say "nothing wrong", and a positive claim the query never
   * made. Read PER METRIC rather than off one row, so a column cannot borrow
   * another column's figure.
   */
  const unattributedByMetric: Partial<Record<FunnelMetric, string | null>> = Object.fromEntries(
    FUNNEL_METRICS.map((m) => [m, teamFigures.byMetric[m]?.unattributed ?? null]),
  );

  /*
   * ⚠️ **`null` is NOT `0` here and the `??` preserves that.** `0` is a
   * measurement — no document was excluded; `null` means nothing was computed at
   * all, which is the unattributable-group case. Both render nothing, for
   * different reasons, and `excludedNote` is the single place that decides.
   */
  const excludedByMetric: Partial<Record<FunnelMetric, number | null>> = Object.fromEntries(
    FUNNEL_METRICS.map((m) => [m, teamFigures.byMetric[m]?.excludedByCurrency ?? null]),
  );

  const memberOptions = membersState.members.map((member) => ({
    id: member.id,
    name: personName(member),
  }));

  const figuresCurrency = actualsCurrency(teamFigures);

  /**
   * What the reader calls the band on screen, or `null` for every band.
   *
   * 🔴 **RESOLVED FROM THE MASTER, NEVER FROM A MAP IN `src/`.** *SUPERSEDED:
   * `BILLING_REGION_LABELS[region]`, a literal that does not exist and must not
   * be created.* The four band names are admin-editable columns on
   * `f_billing_region_defaults`, so a client-side map is a second copy of wording
   * the server owns — right on the day it is written, and silently wrong after
   * the first rename, with nothing failing. `useBillingRegionLabels` fetches
   * them; a failed read falls back to the raw slug and reports nothing, which is
   * its own documented rule and the right one here: this is the caption on a
   * panel whose figures are correct either way.
   *
   * ⚠️ **The unbanded band's wording still comes from ONE place** —
   * `NO_REGION_LABEL`, inside the resolver — so *No billing region recorded* is
   * spelled here exactly as the revenue surfaces print it.
   *
   * ⚠️ **`null` stays `null` and is never sent through the resolver.** Every
   * band has a name; the absence of a band has none, and a word for it would be
   * a segment claiming a scope — see the prop's own doc on `TeamFiguresPanel`.
   */
  const billingRegionLabel = useBillingRegionLabels();
  const regionLabel = region === null ? null : billingRegionLabel(region);

  function openTeamMetric(metric: FunnelMetric) {
    if (teamFigures.actualsQuery === null) return;
    setDrill({
      metric,
      params: { ...teamFigures.actualsQuery, metric },
      title: panelDescription([targetMetricLabel(metric), teamLabel(subject), regionLabel]),
    });
  }

  /** A member's figure: only a member whose own read succeeded has a query. */
  function openMemberMetric(userId: number, name: string, metric: FunnelMetric) {
    const query = memberFigures.queryByUser[userId];
    if (query === undefined) return;
    setDrill({
      metric,
      params: { ...query, metric },
      title: panelDescription([targetMetricLabel(metric), name, regionLabel]),
    });
  }

  /*
   * 🔴 **HOW MANY ADVANCED CONTROLS DIFFER FROM THEIR DEFAULT — nothing else.**
   * `DashboardFilterBar`'s header is explicit that this number is the caller's
   * to compute and that it is the only thing standing between a reader and a
   * narrowed figure read as the whole. So it counts STATE, never intent: an
   * empty member box is "every member" and is therefore not a filter, and the
   * figure mode counts when it is not `value` because a collapsed panel
   * otherwise leaves five columns of percentages with no visible control saying
   * why they are percentages.
   *
   * ⚠️ **The billing region is NOT counted, and neither are the currency or the
   * window.** This number exists so a filter that is OUT OF SIGHT still declares
   * itself; all three of those are in the primary row, always rendered, and
   * `Clear` leaves them alone for the same reason — a reader who has chosen a
   * band can see that they have.
   */
  const activeAdvanced =
    (memberFilter.length > 0 ? 1 : 0) + (figureMode !== 'value' ? 1 : 0);

  /**
   * How many of the chosen members are actually on this team.
   *
   * ⚠️ **Matched against the CURRENT roster rather than trusting the chip
   * count.** Switching team leaves the previous team's ids selected, so the
   * number of chips and the number of people the filter can admit come apart —
   * and the second is the one worth saying.
   */
  const selectedMembers = memberOptions.filter((member) =>
    memberFilter.includes(member.id),
  ).length;

  return (
    <div className="flex flex-col gap-4">
      <DashboardFilterBar
        rememberAs="financial-more-filters"
        activeAdvanced={activeAdvanced}
        onClear={() => {
          setMemberFilter([]);
          setFigureMode('value');
        }}
        /*
         * ⚠️ **THE ONE THING THE MOVE COST, SAID BACK.** Until this bar existed
         * the member filter lived on the contribution panel, where its scope was
         * obvious from where it sat; up here it stands beside three controls
         * that redraw the tiles, which invites the reading that the headline
         * figures are narrowed to the chosen people. They are not — and neither
         * is the reconciliation in the table's own footer, which still describes
         * the whole team.
         *
         * ⚠️ Rendered only while somebody IS narrowed. With an empty selection
         * the tiles and the table both cover everyone, so there is no wrong
         * reading available and a standing sentence would be advice rather than
         * a fact.
         *
         * ⚠️ **It describes the FILTER, not the rows on screen.** The breakdown
         * also collapses members who contributed nothing, so a count of what is
         * rendered would disagree with the count under the table — two numbers
         * about the same thing, neither obviously wrong. This one is about what
         * the control admits; that one is about what the switch hid.
         */
        note={
          memberFilter.length > 0 ? (
            <>
              The breakdown is narrowed to {selectedMembers.toLocaleString()} of{' '}
              {memberOptions.length.toLocaleString()} members. The team figures above are not
              narrowed.
            </>
          ) : undefined
        }
        primary={
          <>
            {/*
              TWO ROWS, owner's request 2026-10-11 ("better structured"): the
              first says WHO and in WHAT MONEY (team, region, member, then the
              currency at the row's end); the second says WHEN (the window
              picker, then the Values/Percentage switch at the row's end). Each
              row aligns its labels on one line (`items-start`), and the two
              display switches share the right-hand edge.
            */}
            <div className="flex w-full flex-wrap items-start gap-x-4 gap-y-3">
            {/*
              ✅ **A SEARCHABLE `Combobox` — owner's decision, 2026-09-13.**
              *SUPERSEDED: a `ui/Select` with an inline `<option>` list.* Every
              non-table-column filter dropdown in the app takes one shape, and
              the owner chose that consistency explicitly over the "a handful of
              options needs no search box" reading — which is the argument this
              control would have won, since the group master is short. It now
              matches the Team member picker two slots along rather than being
              the odd one in its own bar.

              ⚠️ **`Combobox` takes no `className`, so the width is set on a
              wrapper** — the same wrapper the `MultiCombobox` below already
              needs, for the same reason.

              ⛔ **NO `emptyOptionLabel`, because there is no empty team.**
              "Whole organisation" is a real subject that is always the first
              row, not the absence of a choice — `teams` is built with it at the
              head and `setSubject` falls back to it. An empty row would offer a
              second spelling of the same thing, and the two would then have to
              be reconciled on every read.
            */}
            <Field label="Team">
              {({ id }) => (
                <div className="w-56 max-w-full">
                  <Combobox
                    id={id}
                    /*
                     * ⚠️ **ALWAYS ON THE LIST, so the placeholder is
                     * unreachable.** `subject` starts as `WHOLE_ORGANISATION`
                     * and is only ever set from `teams.find(…) ?? WHOLE_ORGANISATION`
                     * or from the settle effect's own group — every one of which
                     * is in `teams`. Nothing here comes from the URL, so there
                     * is no stored value to inject back; were the roster ever to
                     * shrink under a chosen group, this would read "Select…"
                     * where the `<select>` read blank, which is the same absence
                     * said out loud.
                     */
                    value={teamValue(subject)}
                    options={teams.map((team) => ({
                      value: teamValue(team),
                      label: teamLabel(team),
                    }))}
                    searchPlaceholder="Search teams…"
                    /*
                     * ⚠️ Disabled only while the list is genuinely unread. A
                     * FAILED read leaves it enabled on "Whole organisation", which
                     * is a real team that needs no group list at all — a control
                     * disabled by a request that did not come back would take away
                     * a working view.
                     *
                     * ⚠️ **STILL `disabled`, deliberately NOT `Combobox`'s
                     * `loading`.** `DealsPage` swapped the two on the same day
                     * for its reference masters, on the grounds that a value
                     * already in the URL must stay clearable while the list
                     * arrives. Neither half applies here: this state is local,
                     * and while the groups are unread the only team that exists
                     * is the one already selected. A later harmonising pass must
                     * not copy that swap across.
                     */
                    disabled={groupsState.loading}
                    onChange={(value) => {
                      const next =
                        teams.find((team) => teamValue(team) === value) ?? WHOLE_ORGANISATION;
                      setSubject(next);
                      setChosenTeam(teamValue(next));
                      /*
                       * 🔴 **THE MEMBER FILTER IS CLEARED WITH THE TEAM, and it
                       * has to be.** It holds USER IDS, and an id from the team
                       * somebody just left is not a narrower view of the new team
                       * — it matches nobody on it. Left standing, the breakdown
                       * empties and the reader is looking at a filter they cannot
                       * see naming people who are not there.
                       *
                       * ⚠️ **The cost, stated rather than hidden: switching away
                       * and back LOSES the selection.** That is the right trade —
                       * a filter that survives is one a reader has to remember
                       * setting, and this one can only ever survive into a team
                       * where it means nothing.
                       *
                       * ⚠️ **`figureMode` is deliberately NOT reset.** Values
                       * against Percentage is a way of READING any team, not a
                       * selection of rows within one, so it carries across
                       * exactly as the currency and the window do.
                       */
                      setMemberFilter([]);
                    }}
                  />
                </div>
              )}
            </Field>

            {/*
              🔴 **PRIMARY, AND IT MAY NEVER MOVE BEHIND THE DISCLOSURE.** The
              currency is the UNIT of every money figure on this tab, and a unit
              a reader has to open something to learn is how somebody comes to
              quote dollars as pounds.

              ⚠️ **Three codes and not the 164-row master.** The server sums
              against ONE base currency per request, so offering every currency
              would let somebody ask for one in which every figure is zero and
              every document excluded — which reads as a dead business rather
              than as a question nobody should have been offered.

              ⚠️ **`FilterSlot`, not `Field`.** `SegmentedControl` renders a
              labelled GROUP of buttons with no single focusable input, so a
              `<label for>` would point at nothing; the slot's plain span and
              the group's own `aria-label` say the same word.
            */}
            {/* The currency control itself closes this row, after Team member. */}

            {/*
              🔴 **PRIMARY, BESIDE THE CURRENCY, AND FOR THE SAME REASON.** The
              two together say what a figure is ABOUT — which companies, in what
              money — and a population a reader has to open something to learn is
              how a regional figure gets quoted as the business's own. Team
              targets are set per region, so this also decides WHICH target the
              tiles are comparing against.

              ⚠️ **`DashboardFilterBar`'s own header files "region" under
              ADVANCED, and this is a deliberate departure.** That list was
              written for a region that narrows a population already defined; here
              it changes what the headline figures MEAN, which is that same
              header's test for primary. It is also moot on this tab: nothing is
              passed as `advanced`, so there is no disclosure to sit behind.

              ⚠️ **A `Combobox`, not a `SegmentedControl` like the currency
              beside it.** Three codes fit on a row and FIVE bands do not — two
              of the labels are three words and one is a sentence — so segmenting
              them would wrap the bar at every width below a desktop. Every
              filter dropdown in this app is searchable, so `Combobox` is also
              simply the house control for a list this long.

              ⚠️ **`emptyOptionLabel` IS THE ABSENCE OF A FILTER, NOT A SIXTH
              BAND.** `'none'` — *No billing region recorded* — is an ordinary
              row in the list and is a real narrowing to the companies nobody has
              classified. "All billing regions" is a different thing entirely: it
              sends no parameter. The two are one row apart and mean opposite
              things, which is exactly why the wording of each is taken from what
              the revenue surfaces already print rather than invented here.

              ⚠️ **NOTHING IS PINNED BACK, because nothing can fall off this
              list.** A `Combobox` falls back to its PLACEHOLDER for a value
              absent from its options, which is how a stored or URL-arrived value
              comes to be silently misreported — but `region` is local `useState`,
              is never read from the URL or a saved set, and **every value it can
              hold is a member of `regionOptions` or is `null`**, which is the
              empty row. If this ever gains a URL key, it needs the stored value
              injected back and labelled.

              ⚠️ **Options come from `regionOptions` — all FIVE bands, in the
              master's own order.** *SUPERSEDED: `BILLING_REGIONS.map(…)`, which
              was four and silently omitted the unbanded companies.* That list is
              `TARGET_BILLING_REGIONS`; a list written out here would agree the
              day it was written and drop the member somebody adds tomorrow.

              ⚠️ **THE LABELS ARE FETCHED, NOT MAPPED.** `billingRegionLabel` is
              the resolver over `f_billing_region_defaults`; there is no
              `BILLING_REGION_LABELS` and there must not be, because an
              administrator may rename a band at any time and a literal map would
              go stale with nothing failing.
            */}
            <Field label="Billing region">
              {({ id }) => (
                /* `Combobox` takes no `className`, so the width is set on a wrapper. */
                <div className="w-60 max-w-full">
                  <Combobox
                    id={id}
                    value={region ?? ''}
                    /*
                     * ⚠️ **`band` IS ANNOTATED, and it is not decoration.**
                     * `regionOptions` is `readonly TargetBillingRegion[]`, so
                     * this infers today — but the annotation is what makes the
                     * `as TargetBillingRegion` in `onChange` below a NARROW
                     * rather than a hopeful cast: both ends of this control are
                     * then pinned to the same union, and a list that stopped
                     * being one fails HERE rather than at the wire.
                     */
                    options={regionOptions.map((band: TargetBillingRegion) => ({
                      value: band,
                      label: billingRegionLabel(band),
                    }))}
                    emptyOptionLabel="All billing regions"
                    placeholder="All billing regions"
                    searchPlaceholder="Search regions…"
                    /*
                     * ⚠️ **`''` IS THE EMPTY ROW AND BECOMES `null`**, which is
                     * the one place the two spellings of "no narrowing" meet.
                     * Everything else can only be a member of `regionOptions`,
                     * because a `Combobox` hands back a `value` it was given.
                     */
                    onChange={(value) =>
                      setRegion(value === '' ? null : (value as TargetBillingRegion))
                    }
                  />
                </div>
              )}
            </Field>

            {/*
              `inline` makes the picker render its `<Field>`s as a bare fragment
              — no wrapper and no bottom border — so its controls share this
              row.

              ⚠️ **Every label here comes from the picker; do not add one.**
              *SUPERSEDED: "The 'Target period' label comes from the picker; do
              not add a second."* **No control by that name exists.** Since step
              one landed the picker asks TWO questions and labels them
              **`Cycle`** and **`Report by`**, followed by a subdivision or a
              From/To pair. The old wording is kept because a reader meeting the
              new words has to be able to tell them from a control that was
              always called that.

              ⚠️ **It sits LAST deliberately.** The field count swings between
              two and four as the mode changes and ALL of the swing is at the
              picker's tail — the cycle control is always rendered, so it never
              moves anything — so nothing after it moves either. A control that
              jumps position when an unrelated control is touched reads as a
              layout fault.

              ⚠️ **`periods` is this panel's OWN list and must never become a
              second read.** `useDefaultTargetPeriod` has already fetched it; a
              picker fetching it again would be a second request per mount, and
              two copies of one list that can disagree about which cycles exist
              while both look right.

              ⚠️ *SUPERSEDED: "against a 120-per-60s per-IP budget shared
              across the office."* It is **12,000/60s** (corrected 2026-09-17).
              Passing the list down is still right, and the reason that
              survives is the second one: two copies of one list can disagree.
              The budget that reason cited is off by a factor of a hundred.
            */}
            {/*
              🔴 **THESE TWO WERE BEHIND THE `Filters` DISCLOSURE UNTIL
              2026-09-13 AND ARE NOW ALWAYS VISIBLE — owner's instruction.**
              `advanced` is not passed at all any more, so `DashboardFilterBar`
              renders NO toggle rather than a toggle onto nothing, which is the
              behaviour that component already documents for an absent slot.

              ✅ **AND IT CLOSES A LATENT DEFECT THAT COMPONENT WARNS ABOUT IN
              ITS OWN HEADER: "AN ACTIVE FILTER MAY NEVER BE INVISIBLE."** The
              guard against that is `activeAdvanced`, a count the CALLER has to
              compute — and **this call site never passed one**, nor an
              `onClear`. So a member filter could be set, the panel shut, and
              the contribution table silently narrowed with **no count, no
              Clear, and nothing on screen naming what narrowed it** — a
              filtered figure read as the business's own. Moving the control
              into the open removes the hazard structurally instead of adding
              the count that was missing.

              ⚠️ **BOTH BELONG IN THE OPEN ON THIS TAB ANYWAY, BY THAT
              COMPONENT'S OWN TEST.** `Show figures as` is not a narrowing at
              all — it changes how every figure READS, which is the primary
              half's definition — and `Team member` changes which people the
              figures describe.
            */}
            {/*
              ⚠️ **THIS NARROWS THE ROWS OF THE BREAKDOWN AND NOTHING ELSE.**
              The reconciliation in that table's footer still describes the
              WHOLE team — it is not recomputed from whoever is on screen. A
              footer that followed the filter would report a residual made of
              the people somebody had just hidden, which is a figure about the
              control rather than about the business.
            */}
            <Field label="Team member">
              {({ id }) => (
                /* `MultiCombobox` takes no `className`, so the width is set on a wrapper. */
                <div className="w-72 max-w-full">
                  <MultiCombobox
                    id={id}
                    /*
                     * ⚠️ **EMPTY MEANS EVERY MEMBER, NOT NO MEMBER.** There is
                     * no "All members" entry to tick, deliberately: it would be
                     * a row selectable alongside the very people it
                     * contradicts, and the two would then have to be reconciled
                     * on every change. The placeholder is what says which of the
                     * two an empty box means — it is the control's own value
                     * slot, not helper text.
                     */
                    placeholder="All members"
                    searchPlaceholder="Filter members"
                    summaryNoun={{ singular: 'member', plural: 'members' }}
                    values={memberFilter.map(String)}
                    onChange={(values) => setMemberFilter(values.map(Number))}
                    options={memberOptions.map((member) => ({
                      value: String(member.id),
                      label: member.name,
                    }))}
                    /*
                     * ⚠️ **`membersState.loading` ALONE, not the combined flag
                     * the table is given.** This control offers the member
                     * LIST; whether each member's FIGURES have arrived is a
                     * different question and is reported inside the table. A
                     * failed read leaves it enabled and is reported there too,
                     * rather than by a control with no explanation.
                     */
                    disabled={membersState.loading}
                    loading={membersState.loading}
                    /*
                     * ⚠️ **Select all here is NOT a synonym for the empty box
                     * above it, and the difference is a SNAPSHOT against a
                     * RULE.** Empty means *every member*, re-resolved from the
                     * roster on each read; an explicit all-selected set is
                     * today's roster pinned, so a member added afterwards is
                     * IN the first and OUT of the second.
                     *
                     * It is offered anyway because the bar's own note reads
                     * "narrowed to 12 of 12 members", which states the pinning
                     * rather than hiding it. *SUPERSEDED 2026-10-16: "the
                     * divergence cannot outlive the screen" — `memberFilter`
                     * is now remembered on the account (filter memory), so a
                     * pinned set does outlive the mount; the note is what
                     * still says so.* ⛔ **That reasoning does not travel**: the same row
                     * is withheld from the quote and invoice line CITIES
                     * pickers precisely because `allCities` IS a stored live
                     * rule there, and the two would diverge silently.
                     */
                    selectAll
                  />
                </div>
              )}
            </Field>

            <FilterSlot label="Currency" className="w-60 sm:ml-auto">
              <SegmentedControl
                label="Currency"
                value={currency}
                options={ANALYTICS_CURRENCY_OPTIONS}
                onChange={setCurrency}
              />
            </FilterSlot>

            <FilterSlot label="Rates" className="w-60">
              <SegmentedControl
                label="Rates"
                value={fxMode}
                options={FX_MODE_OPTIONS}
                onChange={setFxMode}
              />
            </FilterSlot>
            </div>

            <div className="flex w-full flex-wrap items-start gap-x-4 gap-y-3">

            {/*
              ⚠️ *SUPERSEDED 2026-10-11: "the picker stays last".* It now
              leads the second row, and the control after it is pinned to the
              row's right edge, which answers the same concern. The original
              reasoning, kept: ⚠️ **THE PICKER STAYS LAST, AND THE TWO CONTROLS
              ABOVE WENT IN FRONT OF IT RATHER THAN AFTER IT FOR THAT REASON.** Its field
              count swings between two and four as the mode changes and ALL of
              the swing is at its tail, so anything placed after it jumps
              position when somebody touches an unrelated control — which reads
              as a layout fault rather than as a control appearing.

              ⚠️ **Every label here comes from the picker; do not add one.** It
              asks TWO questions, labelled **`Cycle`** and **`Report by`**,
              followed by a subdivision or a From/To pair.

              ⚠️ **`periods` is this panel's OWN list and must never become a
              second read.** `useDefaultTargetPeriod` has already fetched it; a
              picker fetching it again would be a second request per mount, and
              two copies of one list that can disagree about which cycles exist
              while both look right.

              ⚠️ *SUPERSEDED: "against a 120-per-60s per-IP budget shared
              across the office."* It is **12,000/60s** (corrected 2026-09-17).
              Passing the list down is still right, and the reason that
              survives is the second one: two copies of one list can disagree.
              The budget that reason cited is off by a factor of a hundred.

              `inline` makes the picker render its `<Field>`s as a bare fragment
              — no wrapper and no bottom border — so its controls share this row.
            */}
            <TargetWindowPicker
              period={periodState.period}
              periods={periodState.periods}
              state={picker}
              onChange={setPicker}
              inline
            />

            {/*
              ⚠️ **A VIEW CONTROL, AND IT CHANGES NO REQUEST.** Both figures are
              already in hand for every cell, so this re-reads what is on screen
              rather than re-fetching — which is why it stays enabled while the
              member figures are still arriving.

              ⚠️ **NEITHER MODE HIDES THE OTHER FIGURE**; the switch moves the
              emphasis. Nothing a reader was looking at can disappear by
              touching a control they did not understand.

              `sm:ml-auto` pins it to the row's right edge, so the picker's
              field count swinging between two and four as its mode changes
              never moves this control.
            */}
            <FilterSlot label="Show figures as" className="w-60 sm:ml-auto">
              <SegmentedControl
                label="Show figures as"
                value={figureMode}
                options={FIGURE_MODE_OPTIONS}
                onChange={setFigureMode}
              />
            </FilterSlot>
            </div>
          </>
        }
      />

      <TeamFiguresPanel
        subject={subject}
        teamsError={groupsState.error}
        figures={teamFigures}
        periodState={periodState}
        blocked={blocked}
        noPeriod={noPeriod}
        chosenCurrency={currency}
        figuresCurrency={figuresCurrency}
        regionLabel={regionLabel}
        excludedByMetric={excludedByMetric}
        onOpenMetric={measuredWindow === null ? undefined : openTeamMetric}
      />

      <MetricDrilldownModal
        open={drill !== null}
        onClose={() => setDrill(null)}
        params={drill?.params ?? null}
        title={drill?.title ?? ''}
      />

      <TargetsCommissionPanel periodId={commissionPeriodId} />

      <ContributionPanel
        teamSubject={subject}
        rolls={rolls}
        currency={figuresCurrency}
        memberFilter={memberFilter}
        figureMode={figureMode}
        membersLoading={membersState.loading || memberFigures.loading}
        membersError={membersState.error}
        refused={memberFigures.refused}
        unattributedByMetric={unattributedByMetric}
        onOpenMemberMetric={measuredWindow === null ? undefined : openMemberMetric}
      />
    </div>
  );
}

/* -------------------------------------------------------------------------
 * The five figures
 * --------------------------------------------------------------------- */

/**
 * THE HEADLINE. Five tiles, the failures that explain a missing one, and what
 * the figures left out.
 *
 * ⚠️ **The tiles come from `TargetTileGrid` and are NOT re-rendered here.**
 * Every rule about when a figure may be shown as a zero lives in that component,
 * learned from a defect each; a second copy would agree the day it was written
 * and drift on the first correction, and the drift would be silent — a `0` where
 * a `—` belongs looks exactly like a real answer.
 */
function TeamFiguresPanel({
  subject,
  teamsError,
  figures,
  periodState,
  blocked,
  noPeriod,
  chosenCurrency,
  figuresCurrency,
  regionLabel,
  excludedByMetric,
  onOpenMetric,
}: {
  subject: TeamSubject;
  teamsError: string | null;
  figures: ActualsState;
  periodState: ReturnType<typeof useDefaultTargetPeriod>;
  blocked: boolean;
  /**
   * 🔴 **DERIVED FROM THE RESOLVED WINDOW BY THE PARENT, NEVER READ OFF
   * `periodState.noPeriod` HERE.** The hook's flag answers a question about
   * TODAY and disagrees with a cycle a reader chose; see the derivation at the
   * parent. It is a prop rather than a second computation for the same reason
   * `blocked` above it is: one window for the whole tab, resolved once.
   */
  noPeriod: boolean;
  chosenCurrency: string;
  figuresCurrency: string;
  /**
   * 🔴 **THE BAND THESE FIGURES DESCRIBE, or `null` for every band.**
   *
   * ⚠️ **A LABEL, not the region itself, and that is deliberate.** This panel
   * must not hold a second copy of which bands exist or what each is called — one
   * spelling of *No billing region recorded* on this dashboard, resolved at the
   * one place the state lives.
   *
   * ⚠️ **`null` is "every band" and must render as NOTHING, never as a word.**
   * Any wording for it — "All", "Overall" — would be a segment claiming a scope
   * in a line whose other segments are real narrowings, and a reader would take
   * it for a band. The absence of a region segment IS the statement.
   */
  regionLabel: string | null;
  excludedByMetric: Partial<Record<FunnelMetric, number | null>>;
  /** Opens the tile drill-down; absent while there is no window to measure. */
  onOpenMetric?: (metric: FunnelMetric) => void;
}) {
  const headingId = useId();
  const conversionShort = conversionNote({
    fxMode: figures.fxMode,
    translation: figures.translation,
    currency: figuresCurrency,
  });

  const state: TileState = tileState({
    blocked,
    loading: periodState.loading || figures.loading,
    refused: figures.refused,
    error: periodState.error ?? figures.error,
    noPeriod,
    byMetric: figures.byMetric,
  });

  return (
    /*
     * ⚠️ **STICKY, AND `z-20` IS THE CEILING RATHER THAN A PREFERENCE.** The
     * layer ladder in `src/index.css` reserves 0–20 for page content and 30 for
     * app chrome, so this may not go higher: the app's own sticky top bar is
     * `z-30` and must stay above this panel as it scrolls under it.
     *
     * ⚠️ **`bg-surface` is REQUIRED, not decoration.** A `Panel` that inherited a
     * transparent background would let the contribution table read straight
     * through it while scrolling.
     */
    <Panel className="sticky top-0 z-20 overflow-hidden bg-surface">
      {/*
        * ⚠️ **The team's NAME is the description, and it is a fact rather than
        * helper text.** The filter bar scrolls away under this panel, so without
        * it a pinned header carries five figures and no statement of whose they
        * are. `IndividualPanel` titled itself with its subject for the same
        * reason before it was deleted.
        *
        * ⚠️ **It names the SUBJECT and never the WINDOW.** The window note was
        * removed by the owner on 2026-09-05, and if one is ever wanted back it
        * must be read from `figures.window` — what the server says it MEASURED —
        * and never from the picker, which disagrees for as long as a request is
        * in flight.
        *
        * 🔴 **AND IT NAMES THE UNIT, because a figure read out of context has to
        * carry one.** Two rules apply to that half and both are load-bearing:
        *
        * ⚠️ **"money in X" rather than a bare X.** Four of the five metrics are
        * money and `Won deals` is a COUNT, so a bare currency over this grid
        * would put a unit on a figure that has none.
        *
        * ⚠️ **The MEASURED currency, and only while something was measured.**
        * `figuresCurrency` is the server's own answer — see `actualsCurrency` —
        * and naming a unit over a loading, refused or failed panel would be a
        * claim about a currency nothing was summed in. Same suppression, and
        * the same reason, as the measured-in strip below.
        *
        * 🔴 **"Targets and actuals" IS DROPPED WHERE THERE ARE NO TARGETS —
        * 2026-09-11.** Measured on production that day: `f_revenue_targets`
        * holds five rows and **every one belongs to a single USER**, so no
        * organisation target and no group target exists and this panel's five
        * tiles are actuals alone. Naming targets over them is the same
        * overstatement as naming a currency nothing was summed in, one line up
        * — and `state.noTargetsSet` is the flag the tiles already suppress
        * their own "No target set" line by, so the panel and the tiles cannot
        * disagree about it.
        *
        * ⚠️ **It comes BACK the moment a target exists**, which is the whole
        * shape of every suppression on this tab: a row or a phrase that carries
        * no information is hidden, never deleted.
        *
        * 🔴 **AND IT NAMES THE BILLING REGION, because the figures are narrowed
        * to it.** This is the same rule as the currency one line down and as
        * `scopeLabel` on the revenue-by-company tab: a narrowed figure under a
        * caption that does not say what narrowed it is this dashboard's own
        * forbidden "all revenue" claim, and this panel is STICKY — the filter bar
        * that set the band scrolls away underneath it, so for most of the page
        * this line is the only thing on screen saying which companies these five
        * figures are about.
        *
        * ⚠️ **It sits between the team and the unit**, which is the order the
        * sentence reads in: what is counted, whose it is, which companies, in
        * what money.
        *
        * ⚠️ **UNLIKE the currency, it is NOT gated on `state.kind === 'figures'`.**
        * The currency is suppressed on a loading or refused panel because naming
        * a unit claims something was summed in it; a region claims nothing about
        * arithmetic — it says which question was ASKED, which is true whatever
        * came back. Suppressing it would leave a refused panel unable to say what
        * it was refused about.
        */}
      <PanelHeader
        id={headingId}
        title="Team performance"
        description={panelDescription([
          state.kind === 'figures' && state.noTargetsSet ? null : 'Targets and actuals',
          teamLabel(subject),
          regionLabel,
          state.kind === 'figures' ? `money in ${figuresCurrency}` : null,
        ])}
      />

      {/*
        * Rendered independently of the tiles, not `if/else`: a failed team list
        * still leaves the organisation figures below it correct and worth seeing.
        */}
      {teamsError !== null && (
        <TileFailureStrip headline="Couldn’t read the teams." message={teamsError} />
      )}

      {state.kind === 'error' && (
        <TileFailureStrip headline="Couldn’t read these figures." message={state.message} />
      )}

      {/*
        * ⚠️ **A 403 is a REFUSAL and is painted `warning`, never `danger`.** *You
        * may not ask* and *we could not find out* send a reader to different
        * places, and the danger register sends somebody to fix a system that is
        * working exactly as configured.
        *
        * It cannot arrive on a group or organisation subject today — neither
        * carries a refusal — so this is the branch that fires if the owner ever
        * narrows group visibility. Handled rather than left to fall through to
        * the error strip above, which would be a claim about the server.
        */}
      {state.kind === 'refused' && (
        <TileFailureStrip
          headline="You may not read these figures."
          message={state.message}
          tone="warning"
        />
      )}

      <AttributionNote state={figures} />

      {/*
        * 🔴 **WHAT WAS MEASURED, WHERE IT IS NOT WHAT WAS ASKED FOR.**
        *
        * ⚠️ **Compared against the DATA, never against the picker's own state.**
        * That is the same rule the window already keeps: the two disagree for as
        * long as a request is in flight, and where the server answers in a
        * different currency the picker is simply wrong about what is on screen.
        *
        * ⚠️ **`warning`, not `danger`.** Nothing is broken — the figures are
        * real and correctly labelled; they are just not in the currency the
        * reader asked for, which is a fact they need and not a fault to fix.
        *
        * Suppressed while the panel holds no figures, so a loading or refused
        * tab does not make a claim about a currency nothing was measured in.
        *
        * 🔴 **THE STRIP NAMES THE BAND, because it is a sentence ABOUT the
        * figures on screen and those are narrowed.** "These were measured in
        * GBP" over a panel showing one region reads as a statement about the
        * whole business — and this strip fires precisely when something did not
        * go as asked, which is the worst moment to be vague about what it is
        * describing. The clause is dropped entirely for every band rather than
        * worded, for the reason at `regionLabel`.
        *
        * ⚠️ **Parenthesised rather than read as "these ⟨band⟩ figures".** Four
        * of the five labels are places and the fifth is a sentence — *No billing
        * region recorded* — so folding it into the prose produces English that
        * reads as a fault in the app. A parenthetical takes all five.
        */}
      {state.kind === 'figures' && figuresCurrency !== chosenCurrency && (
        <TileFailureStrip
          headline={`Showing figures in ${figuresCurrency}.`}
          message={`These figures${regionLabel === null ? '' : ` (${regionLabel})`} were measured in ${figuresCurrency}, not the ${chosenCurrency} you chose.`}
          tone="warning"
        />
      )}

      {/*
        ⚠️ **The currency passed is the one the SERVER MEASURED IN, never the
        one the picker is set to.** They diverge against an API that predates
        the baseCurrency parameter, and labelling a GBP figure USD is the one
        outcome worse than the understatement this change fixes.
      */}
      <TargetTileGrid
        state={state}
        labelledBy={headingId}
        currency={figuresCurrency}
        onOpenMetric={state.kind === 'figures' ? onOpenMetric : undefined}
      />

      {/*
        * HOW THESE FIGURES WERE ARRIVED AT. One line, wording owned by
        * `CONVERSION_APPROXIMATE_NOTE` — see it for the full reasoning.
        *
        * ⚠️ **NEITHER `warning` NOR A STRIP.** Nothing is wrong: the figures are
        * correct and correctly labelled, and a bordered strip in the failure
        * family would put a fault register around an ordinary property of the
        * arithmetic. This is a footnote qualifying a figure, so it takes the
        * `text-2xs text-fg-subtle` register the per-member exclusion line in
        * `ContributionPanel` already uses, with the full statement on `title`.
        *
        * ⚠️ **It sits ABOVE the exclusion strip deliberately**: this one always
        * applies wherever figures exist and frames every number in the grid,
        * where the strip below names specific counts that were dropped. General
        * qualification first, specific count second.
        *
        * ⚠️ **Suppressed unless the panel actually holds figures**, the same
        * gate and the same reason as the measured-in-currency strip above — a
        * disclaimer over a loading, refused or failed tab is a claim about a
        * conversion that never happened.
        *
        * ⚠️ **NOT helper text, and a sweep must not take it.** It states a fact
        * about the data the reader cannot otherwise see — nothing distinguishes
        * a natively-summed total from a restated one — which is the documented
        * exception, the same class as the kanban board's "Indicative — mixed
        * GBP, USD" and the revenue tracker's "Mixed currencies". The wrong
        * conclusion it prevents is "this total is what the accounts will show".
        *
        * ⚠️ **By the mode the SERVER ECHOED (owner 2026-10-28), never the
        * control's state** — the same rule as the measured-in currency. An
        * absent echo (an older API) is folded to `'document'` / `null` by the
        * hook, never here. The
        * `detail` title describes document dates only, so it rides only on the
        * document sentence.
        */}
      {state.kind === 'figures' && (
        <p
          className="m-0 px-4 pt-2 text-2xs text-fg-subtle"
          title={conversionShort === CONVERSION_APPROXIMATE_NOTE.short ? CONVERSION_APPROXIMATE_NOTE.detail : undefined}
        >
          {conversionShort}
        </p>
      )}

      <ExcludedByCurrencyStrip excludedByMetric={excludedByMetric} />
    </Panel>
  );
}

/**
 * WHAT THE FIVE FIGURES LEFT OUT, once, under the tiles.
 *
 * 🔴 **THIS EXISTS BECAUSE THE COUNT CANNOT GO INSIDE A TILE FROM HERE.**
 * `TileFigures` owns the inside of a tile and hardcodes no exclusion line, and
 * `TargetTiles` is outside this folder. Beside the grid is the closest a caller
 * can get. **When `TileFigures` grows a per-tile line, delete this strip** —
 * saying it twice is how a reader learns to stop reading it.
 *
 * ⚠️ **The metric is NAMED on every item.** A bare "234 in another currency"
 * under five tiles cannot say which figure it qualifies, and the whole point is
 * that a reader can tell how much of WHICH number is missing.
 *
 * ⚠️ **Nothing renders at zero or at null**, and `excludedNote` is the single
 * place that decides — see it for why `0` printing an "0 excluded" line on every
 * window is the noise that stops the one that says 234 from being read.
 */
function ExcludedByCurrencyStrip({
  excludedByMetric,
}: {
  excludedByMetric: Partial<Record<FunnelMetric, number | null>>;
}) {
  const items: { metric: FunnelMetric; note: ExcludedNote }[] = [];
  for (const metric of FUNNEL_METRICS) {
    const note = excludedByCurrencyNote(excludedByMetric[metric] ?? null);
    if (note !== null) items.push({ metric, note });
  }

  if (items.length === 0) return null;

  return (
    /*
     * ⚠️ **THE LIST IS LED BY WHAT IT IS, and that is the one thing added here.**
     * Five bare items reading "Collected revenue: 14 in another currency" sit
     * under a grid of five figures with nothing saying whether they are part of
     * those figures or missing from them — which is the entire point. The term
     * is a heading for the list, not advice: it answers *what am I looking at*,
     * which the no-helper-text rule already exempts.
     */
    <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1 border-t border-border px-4 py-2.5 text-xs text-fg-subtle">
      <span className="font-medium text-fg-muted">Left out of these figures</span>
      <ul className="m-0 flex list-none flex-wrap gap-x-5 gap-y-1 p-0">
        {items.map(({ metric, note }) => (
          <li key={metric} title={note.detail}>
            <span className="font-medium text-fg-muted">{targetMetricLabel(metric)}</span>:{' '}
            {note.short}
          </li>
        ))}
      </ul>
    </div>
  );
}
